import { TranslocoService } from '@jsverse/transloco';
import { NestedTreeControl } from '@angular/cdk/tree';
import { Component, Input, OnInit, OnChanges, SimpleChanges, ViewChild, TemplateRef, Output, EventEmitter, ViewContainerRef, OnDestroy } from '@angular/core';
import { MatTree, MatTreeNestedDataSource } from '@angular/material/tree';
import { Subscription } from 'rxjs';
import { DirectoryDto } from 'src/app/data-transfer/entities/directory-dto';
import { ColorThemeService, COLOR_THEME_DARK, COLOR_THEME_NAME } from 'src/app/navigation/services/color-theme-service';
import { ContextMenuService } from '../../services/context-menu-service';

export const RECYCLING_BIN_ID = -1;

@Component({
  selector: 'app-directory-tree',
  templateUrl: './directory-tree.component.html',
  styleUrls: ['./directory-tree.component.scss'],
})
export class DirectoryTreeComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('nestedTree') nestedTree!: MatTree<any>;

  @Input() rootFolder!: DirectoryDto;
  @Input() includeRecyclingBin = true;
  @Input() isRecyclingBinDisabled = false;
  @Input() contextMenu!: TemplateRef<any>;
  @Input() selectedFolderIds: number[] = [];
  @Input() selectable = false;
  @Input() autoSelectSubfolders = true;
  @Input() expandedFolderIds: number[] = [];
  @Input() disabledFolderIds: ReadonlySet<number> = new Set();

  @Output() contextMenuOpened = new EventEmitter<DirectoryDto>();
  @Output() foldersSelected = new EventEmitter<number[]>();
  @Output() folderActivated = new EventEmitter<DirectoryDto>();
  @Output() recyclingBinSelected = new EventEmitter<DirectoryDto>();
  @Output() autoSelectSubfoldersChanged = new EventEmitter<boolean>();
  @Output() expandedFolderIdsChanged = new EventEmitter<number[]>();

  nestedTreeControl = new NestedTreeControl<DirectoryDto>((node: DirectoryDto) => node.childDirectories);
  treeDataSource: MatTreeNestedDataSource<DirectoryDto>;
  selectedFolder?: DirectoryDto;
  recyclingBinDtoFolder!: DirectoryDto;
  isDarkTheme = false;

  private themeSubscription?: Subscription;
  private contextMenuClosedSubscription?: Subscription;

  constructor(
    private contextMenuService: ContextMenuService,
    private viewContainerRef: ViewContainerRef,
    private translateService: TranslocoService,
    private colorThemeService: ColorThemeService
  ) {
    this.treeDataSource = new MatTreeNestedDataSource<DirectoryDto>();
    this.themeSubscription = this.colorThemeService.colorThemeSubject.subscribe(nextValue => {
      this.isDarkTheme = nextValue === COLOR_THEME_DARK;
    });
  }

  ngOnInit(): void {
    this.treeDataSource.data = this.getTopFolders();
    this.recyclingBinDtoFolder = this.getRecyclingBinFolder();

    if (this.includeRecyclingBin && this.treeDataSource) {
      this.treeDataSource.data.push(this.recyclingBinDtoFolder);
    }
    this.nestedTreeControl.dataNodes = this.getTopFolders();
    this.isDarkTheme = localStorage.getItem(COLOR_THEME_NAME) === COLOR_THEME_DARK;

    if (this.expandedFolderIds.length > 0) {
      this.restoreExpansion(this.rootFolder, this.expandedFolderIds);
    }
    this.contextMenuClosedSubscription = this.contextMenuService.contextMenuClosed.subscribe(() => {
      this.selectedFolder = undefined;
    });
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['expandedFolderIds'] && this.rootFolder) {
      this.restoreExpansion(this.rootFolder, this.expandedFolderIds);
    }
  }

  private restoreExpansion(root: DirectoryDto, ids: number[]) {
    if (ids.includes(root.id)) {
      this.nestedTreeControl.expand(root);
    }
    root.childDirectories?.forEach(child => this.restoreExpansion(child, ids));
  }

  onTreeInteraction(): void {
    const expandedNodes = this.nestedTreeControl.expansionModel.selected;
    const ids = expandedNodes.map(node => node.id);
    this.expandedFolderIdsChanged.emit(ids);
  }

  private getRecyclingBinFolder() {
    const recyclingBinDtoFolder = new DirectoryDto();
    recyclingBinDtoFolder.id = RECYCLING_BIN_ID;
    recyclingBinDtoFolder.name = this.translateService.translate('dataManagement.directory.recyclingBin');
    return recyclingBinDtoFolder;
  }

  private getTopFolders(): DirectoryDto[] {
    return this.rootFolder.id > 0 ? [this.rootFolder] : [];
  }

  hasChild(node: DirectoryDto): boolean {
    return node.childDirectories && node.childDirectories.length > 0;
  }
  openContextMenu(event: MouseEvent, folder: DirectoryDto) {
    if (this.contextMenu) {
      this.contextMenuService.openContextMenu(this.contextMenu, this.viewContainerRef, event, folder);
    }
    this.selectedFolder = folder;
    this.contextMenuOpened.emit(folder);
  }

  toggleFolder(node: DirectoryDto): void {
    if (!this.selectable) {
      if (this.disabledFolderIds.has(node.id)) {
        return;
      }
      this.selectedFolder = node;
      this.folderActivated.emit(node);
      return;
    }
    if (node.id === this.recyclingBinDtoFolder.id) {
      if (this.isRecyclingBinDisabled) {
        return;
      }
      this.selectedFolder = node;
      this.recyclingBinSelected.emit(node);
      return;
    }
    if (this.selectedFolder?.id === this.recyclingBinDtoFolder.id) {
      this.selectedFolder = undefined;
    }
    const updatedSelection = new Set(this.selectedFolderIds);

    const shouldSelect = !updatedSelection.has(node.id);
    this.setFolderSelection(updatedSelection, node, shouldSelect);

    this.foldersSelected.emit(Array.from(updatedSelection));
  }

  clearSelection(): void {
    this.foldersSelected.emit([]);
  }

  private setFolderSelection(selection: Set<number>, node: DirectoryDto, selected: boolean): void {
    if (selected) {
      selection.add(node.id);
    } else {
      selection.delete(node.id);
    }

    if (this.autoSelectSubfolders) {
      node.childDirectories?.forEach(child => this.setFolderSelection(selection, child, selected));
    }
  }

  isSelected(node: DirectoryDto): boolean {
    return this.selectedFolderIds.includes(node.id);
  }

  onAutoSelectToggleChanged(enabled: boolean): void {
    this.autoSelectSubfoldersChanged.emit(enabled);
  }

  updateDirectoryTreeData(updatedRootFolder: DirectoryDto) {
    this.rootFolder = updatedRootFolder;
    this.treeDataSource.data = [];
    let allFoldersList = this.getTopFolders();
    allFoldersList = allFoldersList.concat(this.recyclingBinDtoFolder);
    this.treeDataSource.data = allFoldersList;
    this.nestedTreeControl.dataNodes = allFoldersList;
    this.nestedTree.renderNodeChanges(this.treeDataSource.data);

    this.nestedTreeControl.collapseAll();
    if (this.expandedFolderIds.length > 0) {
      this.restoreExpansion(this.rootFolder, this.expandedFolderIds);
    }
  }

  ngOnDestroy(): void {
    this.themeSubscription?.unsubscribe();
    this.contextMenuClosedSubscription?.unsubscribe();
  }
}
